import { type StatsigLayerName, useLayer } from '@coursera-web/statsig';

import { get as getUser } from 'js/lib/user';

import { useProductFeatures } from 'bundles/enroll-common/utils/productFeatureFlags';
import { IdTypes } from 'bundles/product-features';

const LAYER_NAME: StatsigLayerName = 'revoke-access-and-limited-free-trial-layer';
const PARAM_NAME = 'revoke-and-limited-trial-variant';

type Variant = 'control' | 'revoke-access' | 'limited-free-trial' | 'revoke-and-limited-trial';

// Excludes pre-existing subscribers (MONETIZE-3123/3153) by only reading (and exposing) the layer once loaded and not excluded.
const useRevokeAccessAndLimitedFreeTrialVariant = (): { variant: Variant; loading: boolean } => {
  const { features, loading, error } = useProductFeatures({
    idType: IdTypes.User,
    id: String(getUser().id),
  });
  const isExcludedFromExperiment = features.get(
    'commerceMonetization',
    'excludeLearnerSubscribedPriorToCurrentMonetizationExperiment'
  );
  const layer = useLayer(LAYER_NAME);
  if (loading) {
    return { variant: 'control', loading: true };
  }
  // Fail closed: an errored exclusion check is treated as excluded, not as eligible.
  if (error || isExcludedFromExperiment) {
    return { variant: 'control', loading: false };
  }
  return { variant: layer.get(PARAM_NAME, 'control') as Variant, loading: false };
};

// https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/layers/revoke-access-and-limited-free-trial-layer
export const useIsRevokeAccessEnabled = (): { isRevokeAccessEnabled: boolean; loading: boolean } => {
  const { variant, loading } = useRevokeAccessAndLimitedFreeTrialVariant();
  return { isRevokeAccessEnabled: variant === 'revoke-access' || variant === 'revoke-and-limited-trial', loading };
};

// https://console.statsig.com/2aQACzHI81tdbY41wEZm3H/layers/revoke-access-and-limited-free-trial-layer
export const useIsLimitedFreeTrialEnabled = (): { isLimitedFreeTrialEnabled: boolean; loading: boolean } => {
  const { variant, loading } = useRevokeAccessAndLimitedFreeTrialVariant();
  return {
    isLimitedFreeTrialEnabled: variant === 'limited-free-trial' || variant === 'revoke-and-limited-trial',
    loading,
  };
};
